import { Inline } from 'braid-design-system';
import { Limited } from './components/Recommended/Recommended.tsx';
import type { PageGroup } from './page.ts';

export const site: PageGroup[] = [
  {
    children: [
      {
        label: 'Home',
        path: '/',
        type: 'standalone',
        icon: 'Home',
      },
    ],
  },
  {
    label: 'Getting started',
    children: [
      {
        label: 'Introduction',
        path: '/introduction',
        type: 'reviewed',
        icon: 'Introduction',
      },
      {
        label: 'Auth',
        path: '/auth',
        type: 'reviewed',
        icon: 'Auth',
        children: [
          {
            label: 'Partner tokens',
            path: '/auth/partner-tokens',
            type: 'reviewed',
          },
          {
            label: 'Browser tokens',
            path: '/auth/browser-tokens',
            type: 'reviewed',
          },
          {
            label: 'Hirer relationships',
            path: '/auth/hirer-relationships',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'GraphQL',
        path: '/graphql',
        type: 'reviewed',
        icon: 'GraphQL',
        children: [
          {
            label: 'Playground',
            path: '/graphql/playground',
            type: 'reviewed',
            hiddenKeywords: ['sandbox'],
          },
          {
            label: 'Mock objects',
            path: '/graphql/playground/mock-objects',
            type: 'reviewed',
          },
          {
            label: 'In practice',
            path: '/graphql/in-practice',
            type: 'reviewed',
          },
          {
            label: 'SEEK API conventions',
            path: '/graphql/seek-api-conventions',
            type: 'reviewed',
          },
          {
            label: 'Error responses',
            path: '/graphql/error-responses',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'Web components',
        path: '/introduction/web-components',
        type: 'reviewed',
        icon: 'Panel',
        children: [
          {
            label: 'Panels',
            path: '/introduction/panels',
            type: 'reviewed',
          },
          {
            label: 'Browser support',
            path: '/introduction/browser-support',
            type: 'reviewed',
          },
          {
            label: 'Assets',
            path: '/introduction/assets',
            type: 'draft',
          },
        ],
      },
      {
        label: 'Events',
        path: '/events',
        type: 'reviewed',
        icon: 'Webhook',
        children: [
          {
            label: 'Webhooks',
            path: '/events/webhooks',
            type: 'reviewed',
          },
          {
            label: 'Polling',
            path: '/events/polling',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'Release notes',
        path: '/introduction/release-notes',
        type: 'standalone',
        icon: 'ReleaseNotes',
      },
    ],
  },
  {
    label: 'Use cases',
    children: [
      {
        label: 'Job Posting',
        path: '/use-cases/job-posting',
        type: 'reviewed',
        icon: 'JobPosting',
        children: [
          {
            label: 'Key concepts',
            path: '/use-cases/job-posting/key-concepts',
            type: 'reviewed',
            children: [
              {
                label: 'Contact people',
                path: '/use-cases/job-posting/contact-people',
                type: 'reviewed',
              },
              {
                label: 'Identifiers & references',
                path: '/use-cases/job-posting/identifiers-and-references',
                type: 'reviewed',
              },
              {
                label: 'Application methods',
                path: '/use-cases/job-posting/application-methods',
                type: 'reviewed',
                hiddenKeywords: ['linkout'],
              },
              {
                label: 'Job ad duplication',
                path: '/use-cases/job-posting/job-ad-duplication',
                type: 'draft',
                hiddenKeywords: ['dupe', 'dedupe'],
              },
            ],
          },
          {
            label: 'Ad Sync',
            path: '/use-cases/job-posting/ad-sync',
            type: 'reviewed',
            children: [
              {
                label: 'User experience guide',
                path: '/use-cases/job-posting/ad-sync/user-experience-guide',
                type: 'reviewed',
                hiddenKeywords: ['Ad Sync UX guide'],
              },
              {
                label: 'Sync positions',
                path: '/use-cases/job-posting/ad-sync/sync-positions',
                type: 'reviewed',
              },
              {
                label: 'Position routing',
                path: '/use-cases/job-posting/ad-sync/position-routing',
                type: 'reviewed',
              },
              {
                label: 'Display job ads',
                path: '/use-cases/job-posting/ad-sync/display-job-ads',
                type: 'reviewed',
              },
              {
                label: 'Transition hirers',
                path: '/use-cases/job-posting/ad-sync/transition-hirers',
                type: 'reviewed',
              },
              {
                label: 'Position not posted reasons',
                path: '/use-cases/job-posting/ad-sync/query-position-not-posted-reason',
                type: 'reviewed',
              },
            ],
          },
          {
            label: 'Enhanced Job Posting',
            path: '/use-cases/job-posting/enhanced',
            type: 'unstable',
            children: [
              {
                label: 'Display panel',
                path: '/use-cases/job-posting/enhanced/display-panel',
                type: 'unstable',
                children: [
                  {
                    label: 'Position input',
                    path: '/use-cases/job-posting/enhanced/position-input',
                    type: 'unstable',
                  },
                ],
              },
              {
                label: 'Manage drafts',
                path: '/use-cases/job-posting/enhanced/manage-drafts',
                type: 'unstable',
              },
              {
                label: 'Post a draft job ad',
                path: '/use-cases/job-posting/enhanced/post-a-draft-job-ad',
                type: 'unstable',
              },
              {
                label: 'Update a job ad',
                path: '/use-cases/job-posting/enhanced/update-a-job-ad',
                type: 'unstable',
              },
            ],
          },
          {
            label: 'Classic Job Posting',
            path: '/use-cases/job-posting/classic',
            type: 'reviewed',
            children: [
              {
                label: 'User experience guide',
                path: '/use-cases/job-posting/user-experience-guide',
                type: 'reviewed',
                hiddenKeywords: ['UX guide'],
              },
              {
                label: 'Job posting features',
                path: '/use-cases/job-posting/features',
                type: 'reviewed',
                children: [
                  {
                    label: 'Position openings',
                    path: '/use-cases/job-posting/position-openings',
                    type: 'reviewed',
                  },
                  {
                    label: 'Position profiles',
                    path: '/use-cases/job-posting/position-profiles',
                    type: 'reviewed',
                  },
                  {
                    label: 'Locations',
                    path: '/use-cases/job-posting/locations',
                    type: 'reviewed',
                  },
                  {
                    label: 'Job categories',
                    path: '/use-cases/job-posting/job-categories',
                    type: 'reviewed',
                    hiddenKeywords: ['classification', 'subclassification'],
                  },
                  {
                    label: 'Salary',
                    path: '/use-cases/job-posting/salary',
                    type: 'reviewed',
                  },
                  {
                    label: 'Ad selection',
                    path: '/use-cases/job-posting/ad-selection',
                    type: 'reviewed',
                    children: [
                      {
                        label: 'Option 1: Panel',
                        path: '/use-cases/job-posting/ad-selection/v2/panel',
                        type: 'reviewed',
                      },
                      {
                        label: 'Option 2: GraphQL',
                        path: '/use-cases/job-posting/ad-selection/v2/graphql',
                        type: 'reviewed',
                      },
                      {
                        label: 'Features',
                        path: '/use-cases/job-posting/ad-selection/v2/features',
                        type: 'reviewed',
                      },
                      {
                        label: 'Testing',
                        path: '/use-cases/job-posting/ad-selection/v2/testing',
                        type: 'reviewed',
                      },
                    ],
                  },
                  {
                    label: 'Branding',
                    path: '/use-cases/job-posting/branding',
                    type: 'reviewed',
                  },
                  {
                    label: 'Questionnaires',
                    path: '/use-cases/job-posting/questionnaires',
                    type: 'reviewed',
                    hiddenKeywords: ['Role Requirements', 'screen'],
                    children: [
                      {
                        label: 'Panel',
                        path: '/use-cases/job-posting/questionnaires/v2/panel',
                        type: 'reviewed',
                      },
                      {
                        label: 'Custom panel',
                        path: '/use-cases/job-posting/questionnaires/custom-panel',
                        type: 'unstable',
                        hidden: true,
                      },
                      {
                        label: 'ATS Screening Questions',
                        path: '/use-cases/job-posting/questionnaires/ats-questions',
                        type: 'reviewed',
                      },
                    ],
                  },
                ],
              },
              {
                label: 'Managing job ads',
                path: '/use-cases/job-posting/managing-job-ads',
                type: 'reviewed',
                children: [
                  {
                    label: 'Previewing a job ad',
                    path: '/use-cases/job-posting/managing-job-ads/previewing-a-job-ad',
                    type: 'reviewed',
                  },
                  {
                    label: 'Posting a job ad',
                    path: '/use-cases/job-posting/managing-job-ads/posting-a-job-ad',
                    type: 'reviewed',
                  },
                  {
                    label: 'Querying a job ad',
                    path: '/use-cases/job-posting/managing-job-ads/querying-a-job-ad',
                    type: 'reviewed',
                  },
                  {
                    label: 'Updating a job ad',
                    path: '/use-cases/job-posting/managing-job-ads/updating-a-job-ad',
                    type: 'reviewed',
                  },
                  {
                    label: 'Closing a job ad',
                    path: '/use-cases/job-posting/managing-job-ads/closing-a-job-ad',
                    type: 'reviewed',
                    hiddenKeywords: ['expire', 'expiry'],
                  },
                ],
              },
            ],
          },
          {
            label: 'Reflecting job ads',
            path: '/use-cases/job-posting/reflecting-job-ads',
            type: 'reviewed',
            hiddenKeywords: ['mirror', 'mirroring'],
          },
          {
            label: 'Archived',
            path: '/use-cases/job-posting/archived',
            type: 'reviewed',
            children: [
              {
                label: 'Questionnaires (V1)',
                path: '/use-cases/job-posting/archived/questionnaires',
                replacement: '/use-cases/job-posting/questionnaires',
                type: 'archived',
                children: [
                  {
                    label: 'Panel',
                    path: '/use-cases/job-posting/questionnaires/panel',
                    replacement:
                      '/use-cases/job-posting/questionnaires/v2/panel',
                    type: 'archived',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        label: 'Optimised Apply',
        path: '/use-cases/application-export',
        type: 'reviewed',
        icon: 'OptimisedApply',
        children: [
          {
            label: 'Candidate profiles',
            path: '/use-cases/application-export/candidate-profiles',
            type: 'reviewed',
          },
          {
            label: 'Attachments',
            path: '/use-cases/application-export/attachments',
            type: 'reviewed',
          },
          {
            label: 'Questionnaire submissions',
            path: '/use-cases/application-export/questionnaire-submissions',
            type: 'reviewed',
          },
          {
            label: 'User experience guide',
            path: '/use-cases/application-export/user-experience-guide',
            type: 'reviewed',
            hiddenKeywords: ['UX guide'],
          },
        ],
      },
      {
        label: 'Apply with SEEK',
        path: '/use-cases/apply-with-seek',
        icon: 'ApplyWithSeek',
        type: 'reviewed',
        children: [
          {
            label: '1. Display button',
            path: '/use-cases/apply-with-seek/display-button',
            type: 'reviewed',
            children: [
              {
                label: 'Brand guidelines',
                path: '/use-cases/apply-with-seek/brand-guidelines',
                type: 'reviewed',
              },
            ],
          },
          {
            label: '2. Handle authorization',
            path: '/use-cases/apply-with-seek/handle-authorization',
            type: 'reviewed',
          },
          {
            label: '3. Retrieve SEEK Profile',
            path: '/use-cases/apply-with-seek/retrieve-seek-profile',
            type: 'reviewed',
          },
          {
            label: '4. Download resume',
            path: '/use-cases/apply-with-seek/download-resume',
            type: 'reviewed',
          },
          {
            label: '5. Populate form',
            path: '/use-cases/apply-with-seek/populate-form',
            type: 'reviewed',
          },
          {
            label: '6. Send application complete signal',
            path: '/use-cases/apply-with-seek/send-application-complete-signal',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'Ad Performance',
        path: '/use-cases/ad-performance',
        icon: 'AdPerformance',
        type: 'reviewed',
        children: [
          {
            label: 'Application complete signal',
            path: '/use-cases/ad-performance/application-complete-signal',
            type: 'reviewed',
          },
          {
            label: 'Panel',
            path: '/use-cases/ad-performance/panel',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'Talent Search Connect',
        path: '/use-cases/proactive-sourcing',
        type: 'unstable',
        icon: 'TalentSearchConnect',
        hidden: true,
        children: [
          {
            label: 'Widgets',
            path: '/use-cases/proactive-sourcing/widgets',
            type: 'unstable',
          },
          {
            label: 'Sharing a candidate',
            path: '/use-cases/proactive-sourcing/sharing-a-candidate',
            type: 'unstable',
          },
          {
            label: 'Interaction history',
            path: '/use-cases/proactive-sourcing/interaction-history',
            type: 'unstable',
          },
          {
            label: 'Unposted positions',
            path: '/use-cases/proactive-sourcing/unposted-positions',
            type: 'unstable',
          },
          {
            label: 'Profile purchases',
            path: '/use-cases/proactive-sourcing/profile-purchases',
            type: 'unstable',
          },
        ],
      },
      {
        label: 'SEEK Profile',
        path: '/use-cases/seek-profile',
        type: 'unstable',
        icon: 'UserProfile',
        children: [
          {
            label: 'Verify with SEEK Pass',
            reactLabel: () => (
              <Inline space="xxsmall" alignY="center">
                <>Verify with SEEK Pass</>
                <Limited compact />
              </Inline>
            ),
            path: '/use-cases/seek-profile/verify-with-seek-pass',
            type: 'unstable',
          },
          {
            label: 'Display candidate summary and actions',
            path: '/use-cases/seek-profile/summary',
            type: 'unstable',
          },
          {
            label: 'Display panel',
            path: '/use-cases/seek-profile/panel',
            type: 'unstable',
          },
        ],
      },
      {
        icon: 'Signals',
        label: 'Selection Signals',
        path: '/use-cases/selection-signals',
        type: 'reviewed',
        children: [
          {
            label: 'Data structure',
            path: '/use-cases/selection-signals/data-structure',
            type: 'reviewed',
          },
          {
            label: 'Application signals',
            path: '/use-cases/selection-signals/application-signals',
            type: 'reviewed',
          },
          {
            label: 'Position signals',
            path: '/use-cases/selection-signals/position-signals',
            type: 'reviewed',
          },
          {
            label: 'Sending signals',
            path: '/use-cases/selection-signals/sending-signals',
            type: 'reviewed',
          },
        ],
      },
    ],
  },
  {
    label: 'How-to guides',
    children: [
      {
        label: 'Overview',
        path: '/guides',
        type: 'reviewed',
        icon: 'GuideIndex',
      },
      {
        label: '2026 Partner Program',
        path: '/guides/2026-partner-program',
        type: 'draft',
        icon: 'Guide',
        hidden: true,
      },
      {
        label: '2025 Apply with SEEK revision',
        path: '/guides/2025-revision',
        type: 'reviewed',
        icon: 'Guide',
        children: [
          {
            label: 'Feature comparison',
            path: '/guides/2025-revision/awsk-feature-comparison',
            type: 'reviewed',
          },
          {
            label: 'Migration considerations',
            path: '/guides/2025-revision/awsk-migration-considerations',
            type: 'reviewed',
          },
          {
            label: 'Hirer identifiers',
            path: '/guides/2025-revision/hirer-identifiers',
            type: 'reviewed',
          },
        ],
      },
      {
        label: 'Monitoring and troubleshooting',
        path: '/guides/monitoring-and-troubleshooting',
        type: 'reviewed',
        icon: 'MonitoringAndTroubleshooting',
      },
      {
        label: 'Report a security incident',
        // Warning: This page is linked to from the Terms and Conditions
        // the path should not be removed/updated without updating the Terms and Conditions
        path: '/guides/security-incident',
        type: 'reviewed',
        icon: 'Security',
      },
      {
        label: 'Archived',
        path: '/migration-guides/archived',
        type: 'reviewed',
        icon: 'GuideArchive',
        children: [
          {
            label: '2022 SEEK API migration',
            path: '/migration-guides/overview',
            type: 'archived',
            children: [
              {
                label: 'Job Posting API',
                path: '/migration-guides/job-posting-api',
                type: 'archived',
                hiddenKeywords: ['FLAPI'],
                children: [
                  {
                    label: '1. Auth',
                    path: '/migration-guides/job-posting-api/phase-1-auth',
                    type: 'archived',
                  },
                  {
                    label: '2. Object identifiers',
                    path: '/migration-guides/job-posting-api/phase-2-object-identifiers',
                    type: 'archived',
                  },
                  {
                    label: '3. Ad selection',
                    path: '/migration-guides/job-posting-api/phase-3-ad-selection',
                    type: 'archived',
                  },
                  {
                    label: '4. Managing job ads',
                    path: '/migration-guides/job-posting-api/phase-4-managing-job-ads',
                    type: 'archived',
                  },
                  {
                    label: '5. Event delivery',
                    path: '/migration-guides/job-posting-api/phase-5-event-delivery',
                    type: 'archived',
                  },
                ],
              },
              {
                label: 'SEEK Application Export',
                path: '/migration-guides/sae',
                type: 'archived',
              },
            ],
          },
          {
            label: 'Hirer identifiers',
            path: '/migration-guides/seek-advertiser-ids',
            type: 'archived',
          },
          {
            label: '2023 Job Posting revision',
            path: '/migration-guides/jobstreet-and-jobsdb-uplift',
            type: 'archived',
            hiddenKeywords: ['Asia', 'Jobstreet', 'Jobsdb', 'Unification'],
            children: [
              {
                label: '1. Developer Dashboard',
                path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-1-developer-dashboard',
                type: 'archived',
              },
              {
                label: '2. Job Posting',
                path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting',
                type: 'archived',
                children: [
                  {
                    label: 'Locations',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/locations',
                    type: 'archived',
                  },
                  {
                    label: 'Job categories',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/job-categories',
                    type: 'archived',
                  },
                  {
                    label: 'Salary',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/salary',
                    type: 'archived',
                  },
                  {
                    label: 'Ad selection',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection',
                    type: 'archived',
                    children: [
                      {
                        label: 'Option 1: Panel',
                        path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection/panel',
                        type: 'archived',
                      },
                      {
                        label: 'Option 2: GraphQL',
                        path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection/graphql',
                        type: 'archived',
                        children: [
                          {
                            label: 'User experience guide',
                            path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection/graphql/user-experience-guide',
                            type: 'archived',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    label: 'Branding',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/branding',
                    type: 'archived',
                  },
                  {
                    label: 'Questionnaires',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/questionnaires',
                    type: 'archived',
                  },
                  {
                    label: 'Job ad preview',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/job-ad-preview',
                    type: 'archived',
                  },
                  {
                    label: 'Content localisation',
                    path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/content-localisation',
                    type: 'archived',
                  },
                ],
              },
              {
                label: '3. Apply & Talent Search',
                path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-3-apply-and-proactive-sourcing',
                type: 'archived',
              },
              {
                label: '4. Transitioning hirers',
                path: '/migration-guides/jobstreet-and-jobsdb-uplift/phase-4-transitioning-hirers',
                type: 'archived',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Contact Us',
    children: [
      {
        label: 'New Contact SEEK Form v2 Final',
        path: '/contact',
        type: 'standalone',
        hidden: true,
      },
    ],
  },
];
